import { Address, CheckoutParams, CheckoutSelectors, Consignment, Country, CustomerAddress, CustomerRequestOptions, FormField, RequestOptions, ShippingInitializeOptions, ShippingRequestOptions } from '@bigcommerce/checkout-sdk';
import { withFormik, FormikProps } from 'formik';
import { debounce, noop } from 'lodash';
import React, { PureComponent, ReactNode } from 'react';
import { lazy, object } from 'yup';

import { getAddressCustomFieldsValidationSchema, getAddressValidationSchema, isEqualAddress, mapAddressFromFormValues, mapAddressToFormValues, AddressFormValues } from '../address';
import { withLanguage, WithLanguageProps } from '../locale';
import { Fieldset, Form, FormContext } from '../ui/form';

import hasSelectedShippingOptions from './hasSelectedShippingOptions';
import BillingSameAsShippingField from './BillingSameAsShippingField';
import ShippingAddress from './ShippingAddress';
import { SHIPPING_ADDRESS_FIELDS } from './ShippingAddressFields';
import ShippingFormFooter from './ShippingFormFooter';

export interface SingleShippingFormProps {
    addresses: CustomerAddress[];
    cartHasChanged: boolean;
    consignments: Consignment[];
    countries: Country[];
    countriesWithAutocomplete: string[];
    customerMessage: string;
    googleMapsApiKey?: string;
    isLoading: boolean;
    isShippingStepPending: boolean;
    isMultiShippingMode: boolean;
    methodId?: string;
    shippingAddress?: Address;
    shouldShowSaveAddress?: boolean;
    shouldShowOrderComments: boolean;
    deinitialize(options: ShippingRequestOptions): Promise<CheckoutSelectors>;
    deleteConsignments(): Promise<Address | undefined>;
    getFields(countryCode?: string): FormField[];
    initialize(options: ShippingInitializeOptions): Promise<CheckoutSelectors>;
    onSubmit(values: SingleShippingFormValues): void;
    onUnhandledError?(error: Error): void;
    signOut(options?: CustomerRequestOptions): void;
    updateAddress(address: Partial<Address>, options?: RequestOptions<CheckoutParams>): Promise<CheckoutSelectors>;
}

export interface SingleShippingFormValues {
    billingSameAsShipping: boolean;
    shippingAddress?: AddressFormValues;
    orderComment: string;
}

interface SingleShippingFormState {
    isResettingAddress: boolean;
    isUpdatingShippingData: boolean;
    hasRequestedShippingOptions: boolean;
}

export const SHIPPING_AUTOSAVE_DELAY = 1000;

class SingleShippingForm extends PureComponent<SingleShippingFormProps & WithLanguageProps & FormikProps<SingleShippingFormValues>> {
    static contextType = FormContext;

    state: SingleShippingFormState = {
        isResettingAddress: false,
        isUpdatingShippingData: false,
        hasRequestedShippingOptions: false,
    };

    private debouncedUpdateAddress: any;

    constructor(props: SingleShippingFormProps & WithLanguageProps & FormikProps<SingleShippingFormValues>) {
        super(props);

        const { updateAddress } = this.props;

        this.debouncedUpdateAddress = debounce(async (address: Address, includeShippingOptions: boolean) => {
            try {
                await updateAddress(address, {
                    params: {
                        include: {
                            'consignments.availableShippingOptions': includeShippingOptions,
                        },
                    },
                });
                if (includeShippingOptions) {
                    this.setState({ hasRequestedShippingOptions: true });
                }
            } finally {
                this.setState({ isUpdatingShippingData: false });
            }
        }, SHIPPING_AUTOSAVE_DELAY);
    }

    render(): ReactNode {
        const {
            addresses,
            cartHasChanged,
            isLoading,
            onUnhandledError,
            methodId,
            shouldShowSaveAddress,
            countries,
            countriesWithAutocomplete,
            googleMapsApiKey,
            shippingAddress,
            consignments,
            shouldShowOrderComments,
            initialize,
            isValid,
            deinitialize,
            values: { shippingAddress: addressForm },
            isShippingStepPending,
        } = this.props;

        const {
            isResettingAddress,
            isUpdatingShippingData,
            hasRequestedShippingOptions,
        } = this.state;

        const PAYMENT_METHOD_VALID = ['amazon', 'amazonpay'];
        const shouldShowBillingSameAsShipping = !PAYMENT_METHOD_VALID.some(method => method === methodId);

        return (
            <Form autoComplete="on">
                <Fieldset>
                    <ShippingAddress
                        addresses={ addresses }
                        consignments={ consignments }
                        countries={ countries }
                        countriesWithAutocomplete={ countriesWithAutocomplete }
                        deinitialize={ deinitialize }
                        formFields={ this.getFields(addressForm && addressForm.countryCode) }
                        googleMapsApiKey={ googleMapsApiKey }
                        hasRequestedShippingOptions={ hasRequestedShippingOptions }
                        initialize={ initialize }
                        isLoading={ isResettingAddress }
                        isShippingStepPending={ isShippingStepPending }
                        methodId={ methodId }
                        onAddressSelect={ this.handleAddressSelect }
                        onFieldChange={ this.handleFieldChange }
                        onUnhandledError={ onUnhandledError }
                        onUseNewAddress={ this.onUseNewAddress }
                        shippingAddress={ shippingAddress }
                        shouldShowSaveAddress={ shouldShowSaveAddress }
                    />
                    {
                        shouldShowBillingSameAsShipping && <div className="form-body">
                            <BillingSameAsShippingField />
                        </div>
                    }
                </Fieldset>

                <ShippingFormFooter
                    cartHasChanged={ cartHasChanged }
                    isLoading={ isLoading || isUpdatingShippingData }
                    isMultiShippingMode={ false }
                    shouldDisableSubmit={ this.shouldDisableSubmit() }
                    shouldShowOrderComments={ shouldShowOrderComments }
                    shouldShowShippingOptions={ isValid }
                />
            </Form>
        );
    }

    private shouldDisableSubmit: () => boolean = () => {
        const {
            isLoading,
            consignments,
            isValid,
        } = this.props;

        const {
            isUpdatingShippingData,
        } = this.state;

        if (!isValid) {
            return false;
        }

        return isLoading || isUpdatingShippingData || !hasSelectedShippingOptions(consignments);
    };

    private handleFieldChange: (name: string) => void = async name => {
        const {
            setFieldValue,
        } = this.props;

        if (name === 'countryCode') {
            setFieldValue('shippingAddress.stateOrProvince', '');
            setFieldValue('shippingAddress.stateOrProvinceCode', '');
        }

        // Enqueue the following code to run after Formik has run validation
        await new Promise(resolve => setTimeout(resolve));

        const isShippingField = SHIPPING_ADDRESS_FIELDS.includes(name);

        const { hasRequestedShippingOptions } = this.state;

        const { isValid } = this.props;

        if (!isValid) {
            return;
        }

        this.updateAddressWithFormData(isShippingField || !hasRequestedShippingOptions);
    };

    private updateAddressWithFormData(includeShippingOptions: boolean) {
        const {
            shippingAddress,
            values: { shippingAddress: addressForm },
        } = this.props;

        const updatedShippingAddress = addressForm && mapAddressFromFormValues(addressForm);

        if (!updatedShippingAddress || isEqualAddress(updatedShippingAddress, shippingAddress)) {
            return;
        }

        this.setState({ isUpdatingShippingData: true });
        this.debouncedUpdateAddress(updatedShippingAddress, includeShippingOptions);
    }

    private handleAddressSelect: (
        address: Address
    ) => void = async address => {
        const {
            updateAddress,
            onUnhandledError = noop,
            values,
            setValues,
        } = this.props;

        this.setState({ isResettingAddress: true });

        try {
            await updateAddress(address);

            setValues({
                ...values,
                shippingAddress: mapAddressToFormValues(
                    this.getFields(address.countryCode),
                    address
                ),
            });
        } catch (error) {
            onUnhandledError(error);
        } finally {
            this.setState({ isResettingAddress: false });
        }
    };

    private onUseNewAddress: () => void = async () => {
        const {
            deleteConsignments,
            onUnhandledError = noop,
            setValues,
            values,
        } = this.props;

        this.setState({ isResettingAddress: true });

        try {
            const address = await deleteConsignments();
            setValues({
                ...values,
                shippingAddress: mapAddressToFormValues(
                    this.getFields(address && address.countryCode),
                    address
                ),
            });
        } catch (e) {
            onUnhandledError(e);
        } finally {
            this.setState({ isResettingAddress: false });
        }
    };

    private getFields(countryCode: string | undefined): FormField[] {
        const {
            getFields,
        } = this.props;

        return getFields(countryCode);
    }
}

export default withLanguage(withFormik<SingleShippingFormProps & WithLanguageProps, SingleShippingFormValues>({
    handleSubmit: (values, { props: { onSubmit } }) => {
        onSubmit(values);
    },
    mapPropsToValues: ({ getFields, shippingAddress,  customerMessage }) => ({
        billingSameAsShipping: true,
        orderComment: customerMessage,
        shippingAddress: mapAddressToFormValues(
            getFields(shippingAddress && shippingAddress.countryCode),
            shippingAddress
        ),
    }),
    isInitialValid: ({
        shippingAddress,
        getFields,
        language,
    }) => (
        !!shippingAddress && getAddressValidationSchema({
            language,
            formFields: getFields(shippingAddress.countryCode),
        }).isValidSync(shippingAddress)
    ),
    validationSchema: ({
        language,
        getFields,
        methodId,
    }: SingleShippingFormProps & WithLanguageProps) => methodId ?
        object({
            shippingAddress: lazy<Partial<AddressFormValues>>(formValues =>
                getAddressCustomFieldsValidationSchema({
                    language,
                    formFields: getFields(formValues && formValues.countryCode),
                })
            ),
        }) :
        object({
            shippingAddress: lazy<Partial<AddressFormValues>>(formValues =>
                getAddressValidationSchema({
                    language,
                    formFields: getFields(formValues && formValues.countryCode),
                })
            ),
        }),
    enableReinitialize: false,
})(SingleShippingForm));
